/**
 * ADLS Gen2 (hierarchical namespace) shared types — S8 Task 8.1, pinned in the S8 plan «Pinned additions» (r4) with the
 * S8.0 exit facts (NOTES «S8: live-probed facts»). Process-agnostic; re-exported through `./azureStorage`.
 */
import type { StorageResourceRef } from './azureStorageResource';
import type { StorageErrorCategory } from './azureStorageModel';

export type HnsPathRef = Extract<StorageResourceRef, { kind: 'hnsPath' }>;
/** A path as typed / sent by a caller, BEFORE its kind is known; normalised once by `normalizeHnsPath` (Task 8.2). */
export type HnsLocator = { fileSystem: string; path: string };
/** Service facts of a Data Lake path; present on `folder` / `blob` items of an `hnsPaths` listing only. */
export type StorageHnsFacts = { owner?: string; group?: string; permissions?: string; etag?: string; lastModified?: string };
export type StorageAclPermissions = { read: boolean; write: boolean; execute: boolean };
export type StorageAclScope = 'access' | 'default';
export type StorageAclEntryType = 'user' | 'group' | 'mask' | 'other';
/** `id` = object ID (or the UPN the service returned) of a NAMED user / group; absent for `user::`, `group::`, `mask::`, `other::`. */
export type StorageAclEntry = { scope: StorageAclScope; type: StorageAclEntryType; id?: string; permissions: StorageAclPermissions };
export type StorageHnsAccessControl = {
  resource: HnsPathRef; isDirectory: boolean; owner: string; group: string;
  /** Service string, e.g. `rwxr-x---+` (`+` = extended ACL; `t` / `T` = sticky bit — NOTES S8 row A7). */
  permissions: string; stickyBit: boolean;
  /** Access then default, service order, masks included as loaded. */
  entries: StorageAclEntry[];
  /** Identity of the path for `If-Match`. It does NOT version the ACL: an ACL write leaves a file's ETag unchanged (NOTES S8 row A5). */
  etag: string; lastModified?: string;
  /** Best-effort object-ID → UPN labels from a second `userPrincipalName: true` read (named users only; never used for writes). */
  displayNames?: Record<string, string>;
  /** True when the connection signs with an account key (incl. the RBAC-fix `source: 'arm'` key): ACLs are not enforced for it. */
  superUser: boolean;
};
/** What a single-path save compares with a read right before it writes (the table / queue policy precedent). */
export type StorageAclBaseline = { owner: string; group: string; entries: StorageAclEntry[] };
export type StorageAclChange =
  | { mode: 'set'; entries: StorageAclEntry[] }        // full ACL — validateFullAcl
  | { mode: 'modify'; entries: StorageAclEntry[] }     // validateAclPatch: an explicit mask is REQUIRED in every scope a named entry is added / changed in (E5)
  | { mode: 'remove'; entries: Array<{ scope: StorageAclScope; type: 'user' | 'group'; id: string }> };   // NAMED entries only
/**
 * Per-path Retry state, committed in the MANIFEST (never only in the ledger) — findings S8-R2-02, S8-R3-01.
 * 'selected' = committed by the Retry command with its requeue; 'dispatched' = committed before the write is sent;
 * 'succeeded' = committed before the ledger record goes `done` (the only source of "fixed by Retry"). A Retry run that
 * settles its record failed deletes `retry`.
 */
export type HnsAclRetryState =
  | { state: 'selected'; at: string }
  | { state: 'dispatched'; ifMatch: string; dispatchedAt: string }
  | { state: 'succeeded'; at: string; verified: 'acknowledged' | 'reread' };
/** `seq` = the manifest-assigned identity (never reused within an epoch); `path` relative to the file system, redacted — display only, never a ledger key. */
export type HnsAclFailure = { seq: number; path: string; isDirectory: boolean; category: StorageErrorCategory; code?: string; message: string; retry?: HnsAclRetryState };
/** `cancelled` = the job's own abort (a cancel, a disconnect or a stall); `interrupted` = the app died with the batch in flight. */
export type HnsAclUncertainReason = 'cancelled' | 'timeout' | 'network' | 'interrupted';
/** Durable recursive-ACL state (E1): mutated ONLY by the ACL backend, committed ONLY through `ctx.checkpointJob()`. */
export type TransferJobAcl = {
  /** Starts at 1; Restart increments it; every ledger key of this job carries it. */
  epoch: number;
  target: HnsPathRef;
  change: StorageAclChange;
  batchSize: number;
  /** Root ETag read at approval; re-checked before the FIRST batch of epoch 1 only; cleared by Restart. */
  expectedRootEtag?: string;
  /** Root `x-ms-creation-time` captured before the first batch of an epoch (identity check — NOTES S8 row R9). */
  rootCreatedOn?: string;
  nextFailureSeq: number;
  traversal: {
    phase: 'not-started' | 'in-progress' | 'finished';
    nextToken?: string;
    batchesCommitted: number;
    inFlight?: { batch: number; startToken?: string; dispatchedAt: string };
    uncertain?: { batch: number; startToken?: string; reason: HnsAclUncertainReason };
    /**
     * r4: the root's authoritative failure, committed before the root record goes `failed`. r5: it OUTRANKS `phase` — R11
     * (`AclTraversalUnconfirmed`) commits it with `phase: 'finished'` — so success is read only through `aclTraversalOutcome`.
     */
    failure?: { category: StorageErrorCategory; code?: string; message: string; at: string };
  };
  /** Added ONLY when a batch commits. */
  confirmed: { directories: number; files: number; failed: number };
  failures: HnsAclFailure[];
  failuresOmitted: number;
};
/** An unfinished move (continuation or unknown outcome); the token itself never leaves main. */
export type HnsPendingMove = {
  id: string; connectionId: string; source: HnsPathRef; sourceEtag: string; destination: HnsLocator; startedAt: string; updatedAt: string;
  state: 'ready' | 'uncertain'; requestsAcknowledged: number;
  uncertainRequest?: 'first' | 'continuation';
  /** Computed in MAIN by `canContinueMove` (r4, S8-R3-03) — never an uncertain first request. */
  canContinue: boolean;
  /** The last Continue sent nothing: its guard's reads (opus F8) failed. Cleared when Check again finds they pass, or a continuation leaves (astra-D #4). */
  continueRefused?: true;
};
/**
 * What "Check again" PROVED about the record it removed (review fixes C #1): the move finished (the read-only evidence
 * of `hnsMoveEvidence`) or was never applied. Absent = nothing proven — the record stays, or it was already gone.
 */
export type HnsPendingMoveSettlement =
  | { id: string; outcome: 'moved'; resource: HnsPathRef; evidence: 'verified-file' | 'verified-directory' }
  | { id: string; outcome: 'not-applied' };
export type HnsMoveResult =
  | { state: 'moved'; resource: HnsPathRef; etag?: string; evidence: 'final-response' | 'verified-file' | 'verified-directory' }
  | { state: 'partial'; pendingMoveId: string }
  | { state: 'uncertain'; pendingMoveId: string };

/** Entries per scope, counting `user::`, `group::`, `other::`, `mask::` and every named entry (8.0.3: 33 → 400 `TooManyAclEntries`). */
export const HNS_ACL_MAX_ENTRIES = 32;
/** 8.0.5: a 2,000-path batch took 0.8–2.3 s on `jamsqlazsthns`, far below the 120 s stall detector. */
export const HNS_ACL_BATCH_SIZE_DEFAULT = 2000;
/** = S2's `job.errors` cap (E3). */
export const HNS_ACL_FAILURES_LISTED_MAX = 100;
/** Automatic re-sends of one batch inside a run (8.0.5 REPLAY_SAFE verified). */
export const HNS_ACL_BATCH_REPLAYS_MAX = 3;
/** D17: how long a restored Data Lake tab waits for account info (= main's HOUSEKEEPING_PROBE_DEADLINE_MS; a parity test pins it). */
export const HNS_READINESS_WAIT_MS = 15_000;
/** D15: what Rename / Move and `storage_move_path` say when no rename implementation is selected (`moveImplementation: 'disabled'`). */
export const HNS_MOVE_UNAVAILABLE_REASON = 'Rename and move are not available for Data Lake paths in this version: Azure could not be made to refuse an existing destination reliably.';
